import { z } from 'zod';

/** A block's text ceiling — long prose belongs in several blocks, not one. */
export const LESSON_BLOCK_TEXT_MAX_LENGTH = 4000;

/** How many blocks one lesson may hold, in one locale. */
export const LESSON_CONTENT_MAX_BLOCKS = 200;

/**
 * The lesson body: an ordered list of typed blocks, stored as jsonb, **per
 * locale** (N5).
 *
 * ## Blocks rather than a markup string
 *
 * A stored HTML or markdown string would have to be sanitized on every read by
 * every client, and RTL is where that goes wrong quietly: an author pasting
 * `<div dir="ltr">` from a browser would pin one paragraph the wrong way round
 * in Arabic and nothing would refuse it. A closed block union renders through
 * the clients' own typography — the mobile `Text` styles, the admin prose tokens
 * — so direction, theme and font scale come from the design language rather than
 * from what was pasted (U6, U7).
 *
 * ## There is no image block yet, and that is a scope decision
 *
 * A lesson image would be **platform-owned media that a member may read** — a
 * `lesson_image` kind declaring `memberRead: 'open'`, which since P25a2 the kind
 * registry can express (the venue photograph is the first). What has not changed
 * is whose decision it is: a new kind is an entry in `MEDIA_KINDS`, a policy in
 * the registry, a Postgres enum value that cannot be taken back, an upload route
 * and a byte ceiling — N12's read authorization, not a courses change. So it
 * stays an open issue rather than something smuggled in here, and the mode it
 * would take is now the easy part of it.
 *
 * Extending this union later costs no migration: an older document simply
 * contains no blocks of the new kind, and `kind` is the discriminant every
 * renderer already switches on.
 */
const HeadingBlockSchema = z
  .object({
    kind: z.literal('heading'),
    /**
     * Two levels, not six. The lesson's own title is the h1, so a body heading
     * is a section (2) or a sub-section (3) — and a reader cannot distinguish
     * more than that on a phone.
     */
    level: z.union([z.literal(2), z.literal(3)]),
    text: z.string().trim().min(1).max(LESSON_BLOCK_TEXT_MAX_LENGTH),
  })
  .strict();

const ParagraphBlockSchema = z
  .object({
    kind: z.literal('paragraph'),
    text: z.string().trim().min(1).max(LESSON_BLOCK_TEXT_MAX_LENGTH),
  })
  .strict();

const ListBlockSchema = z
  .object({
    kind: z.literal('list'),
    ordered: z.boolean(),
    items: z.array(z.string().trim().min(1).max(LESSON_BLOCK_TEXT_MAX_LENGTH)).min(1).max(50),
  })
  .strict();

/**
 * A quotation — the block this platform's subject matter needs most, and the
 * reason `attribution` is a field rather than a convention: a hadith or an ayah
 * rendered without its source is exactly the mistake the content exists to
 * avoid, and a citation glued onto the end of the text cannot be styled apart
 * from it.
 */
const QuoteBlockSchema = z
  .object({
    kind: z.literal('quote'),
    text: z.string().trim().min(1).max(LESSON_BLOCK_TEXT_MAX_LENGTH),
    attribution: z.string().trim().min(1).max(200).optional(),
  })
  .strict();

const DividerBlockSchema = z.object({ kind: z.literal('divider') }).strict();

/**
 * Every kind of block, as a list — what an authoring UI offers to add.
 *
 * The union above is the law; this is the same closed set in a form a client can
 * *enumerate*, the way `DISCOUNT_SOURCES` and `STACKING_POLICIES` do for their
 * enums. Without it the admin editor would hand-write the five names, which is a
 * second source of truth for a set the union already fixes — and the copy would
 * be the one that goes stale.
 *
 * `lesson-content.spec.ts` asserts this list and the union's own discriminants
 * are identical, so adding a block to one and not the other fails the suite
 * rather than shipping a kind nobody can author (or an add-menu entry that
 * nothing will parse).
 *
 * **The order is the order the add-menu offers them in**, and it is deliberate:
 * paragraph first because it is what most blocks are, then the two that give a
 * page structure, then the quotation this platform's subject matter needs most,
 * and the divider last because it is punctuation rather than content.
 */
export const LESSON_BLOCK_KINDS = ['paragraph', 'heading', 'list', 'quote', 'divider'] as const;

export type LessonBlockKind = (typeof LESSON_BLOCK_KINDS)[number];

const BLOCK_KIND_SET: ReadonlySet<string> = new Set(LESSON_BLOCK_KINDS);

/** Fail-closed narrowing for a kind off the wire, or out of a stored document. */
export function isLessonBlockKind(value: string): value is LessonBlockKind {
  return BLOCK_KIND_SET.has(value);
}

export const LessonBlockSchema = z.discriminatedUnion('kind', [
  HeadingBlockSchema,
  ParagraphBlockSchema,
  ListBlockSchema,
  QuoteBlockSchema,
  DividerBlockSchema,
]);

export type LessonBlock = z.infer<typeof LessonBlockSchema>;

export const LessonContentSchema = z.array(LessonBlockSchema).min(1).max(LESSON_CONTENT_MAX_BLOCKS);

export type LessonContent = z.infer<typeof LessonContentSchema>;

/**
 * How many characters a reader actually reads — what an estimated reading time
 * is derived from, and what the admin list shows as a "length" column.
 *
 * A quote's `attribution` is excluded: it is a citation the eye skips, and
 * counting it would make a page of one-line hadiths look longer than a page of
 * prose.
 */
export function lessonContentLength(content: LessonContent): number {
  let total = 0;

  for (const block of content) {
    switch (block.kind) {
      case 'heading':
      case 'paragraph':
      case 'quote':
        total += block.text.length;
        break;
      case 'list':
        for (const item of block.items) {
          total += item.length;
        }
        break;
      case 'divider':
        break;
    }
  }

  return total;
}
